import { useRef, useCallback, useEffect, useState } from 'react';

/**
 * An async-aware debounce hook.
 * Prevents firing a new execution if the previous async operation is still pending.
 * If new calls are made during execution, the most recent arguments are queued and fired after the delay once the
 * current execution finishes.
 */
const useAsyncAwareDebounceCallback = <T extends any[]>(
  asyncFunction: (...args: T) => Promise<any>,
  delay: number = 750,
  executionTimeoutMs: number = 60 * 1000 // 1 minute default
): { debouncedFunction: (...args: T) => void; hasUnsavedChanges: boolean } => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isExecutingRef = useRef<boolean>(false);
  const latestArgsRef = useRef<T | null>(null);

  // Track if we have data waiting to be saved or actively saving
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

  const execute = useCallback(async () => {
    // Don't execute if already running
    if (isExecutingRef.current) return;

    // Store latest args
    const args = latestArgsRef.current;
    // Clear pending queue
    latestArgsRef.current = null;

    // Short circuit if no args to send
    if (!args) {
      setHasUnsavedChanges(false);
      return;
    }

    // Assign process to be running
    isExecutingRef.current = true;

    try {
      // Await callback function to complete
      // await asyncFunction(...args);

      // Wrap the execution in Promise.race to guarantee it unblocks
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Async execution timed out')), executionTimeoutMs);
      });
      await Promise.race([asyncFunction(...args), timeoutPromise]);
    } catch (error) {
      // Catch to prevent unhandled promise rejections
      console.warn('Debounced execution rejected or timed out. Queue will unblock.', error);
    } finally {
      // Mark process as finished
      isExecutingRef.current = false;

      if (latestArgsRef.current) {
        // More data passed during previous processing, re-trigger execution again (debounced)
        timeoutRef.current = setTimeout(execute, delay);
      } else {
        // The processing has finished (or failed) and queue is now empty.
        setHasUnsavedChanges(false);
      }
    }
  }, [asyncFunction, delay]);

  const debouncedFunction: (...args: T) => void = useCallback(
    (...args: T): void => {
      setHasUnsavedChanges(true);
      // Always overwrite with latest args
      latestArgsRef.current = args;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Only start timeout if not already executing
      if (!isExecutingRef.current) {
        timeoutRef.current = setTimeout(execute, delay);
      }
    },
    [execute, delay]
  );

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    debouncedFunction,
    hasUnsavedChanges
  };
};

export default useAsyncAwareDebounceCallback;
